import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Typography,
  Divider,
  Paper
} from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { loadBillingFilters, saveBillingFilters } from 'Includes/billingFilterStorage';
import OrdersSourceToggle, { loadOrdersSource } from 'Components/OrdersSourceToggle';
import { TEAM_STATUS_CHOICES } from '../../Includes/teamStatus';
import MapIcon from '@mui/icons-material/Map';
import CoordinatorMapModal from 'Components/CoordinatorMapModal';

function CoordinatorWiseReportList(props) {
  const storedFilters = loadBillingFilters();

  const [academicYearList, setAcademicYearList] = React.useState([])
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '')
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all')
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = React.useState('')
  const [serviceList, setServiceList] = React.useState([])
  const [selectedService, setSelectedService] = React.useState(null)
  const [rows, setRows] = React.useState([])
  const [rowCount, setRowCount] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  })
  const [mapModalOpen, setMapModalOpen] = React.useState(false)
  const [selectedCoordinator, setSelectedCoordinator] = React.useState(null)
  const [orderSource, setOrderSource] = React.useState(loadOrdersSource())

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  React.useEffect(() => {
    getAcademicYearList()
    getServiceList()
  }, [])

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then((res) => {
      if (res?.status === 200) {
        const list = res.data.data || []
        setAcademicYearList(list)

        if (selectedAcademicYear) {
          fetchCoordinatorSummary(selectedAcademicYear)
        }
      }
    })
  }

  const getServiceList = () => {
    getRequest(GET_URL.services?.api, {}, props).then((res) => {
      if (res?.status === 200) {
        setServiceList(res.data.data || [])
      }
    })
  }

  const fetchCoordinatorSummary = (yearId) => {
    if (!yearId) return

    setLoading(true)

    const params = {
      year: yearId,
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
      ...(selectedService?.id && { service: selectedService.id }),
      order_source: orderSource,
    }

    getRequest(GET_URL.billing_config.api, { ...params, coordinator_wise_summary: 1 }, props)
      .then((res) => {
        setLoading(false)
        if (res?.status === 200) {
          const data = res.data?.data || []

          const transformed = data.map((item, index) => ({
            id: index + 1,
            coordinator_id: item.coordinator_id,
            coordinator_name: item.coordinator_name || 'No Coordinator',
            order_total_amount: item.totals?.order || 0,
            issued_invoice_total_amount: item.totals?.issued || 0,
            issue_invoice_total_amount: item.totals?.invoiced || 0,
            paid_total_amount: item.totals?.paid || 0,
            discount_amount: item.totals?.discount || 0,
            balance_amount: item.totals?.balance || 0,
            balance_to_be_paid_by_today: item.totals?.balanceToPayByToday || 0,
            no_of_companies: item.no_of_companies || 0,
          }))

          setRows(transformed)
          setRowCount(res.data?.count || transformed.length)
        }
      })
      .catch(() => setLoading(false))
  }

  React.useEffect(() => {
    if (selectedAcademicYear) {
      fetchCoordinatorSummary(selectedAcademicYear)
    }
  }, [statusByTeamFilter, clientBillingStatusFilter, selectedService, paginationModel.page, paginationModel.pageSize, selectedAcademicYear, orderSource])

  const columns = [
    { field: 'coordinator_name', headerName: 'Coordinator', width: 220 },
    {
      field: 'balance_to_be_paid_by_today',
      headerName: 'Due Today',
      width: 180,
      renderCell: (params) => {
        const val = parseFloat(params.value || 0)
        return (
          <Typography color={val > 0 ? '#d32f2f' : 'inherit'} fontWeight={600}>
            {formatAmount(val)}
          </Typography>
        )
      }
    },
    { field: 'no_of_companies', headerName: 'Companies', width: 140 },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          icon={<MapIcon />}
          label="Map"
          onClick={() => {
            setSelectedCoordinator(params.row)
            setMapModalOpen(true)
          }}
        />
      ]
    }
  ]

  return (
    <Box p={2}>

      {/* HEADER (NO PAPER) */}
      <Box mb={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700}>
            Coordinator Wise Reports
          </Typography>
          <OrdersSourceToggle
            value={orderSource}
            onChange={(next) => {
              setOrderSource(next);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          />
        </Box>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* FILTERS (WITH PAPER) */}
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value)
                  saveBillingFilters({ academicYearId: e.target.value })
                  setPaginationModel((prev) => ({ ...prev, page: 0 }))
                }}
                label="Academic Year"
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Client Status (Team)</InputLabel>
              <Select
                value={statusByTeamFilter}
                label="Client Status (Team)"
                onChange={(e) => {
                  setStatusByTeamFilter(e.target.value)
                  setPaginationModel((prev) => ({ ...prev, page: 0 }))
                }}
              >
                {TEAM_STATUS_CHOICES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <FormControl fullWidth>
              <InputLabel>Billing Status</InputLabel>
              <Select
                value={clientBillingStatusFilter}
                onChange={(e) => setClientBillingStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GOOD">GOOD</MenuItem>
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12}>
            <Autocomplete
              options={serviceList}
              getOptionLabel={(o) => o.name || ''}
              value={selectedService}
              onChange={(_e, v) => setSelectedService(v)}
              renderInput={(params) => (
                <TextField {...params} label="Service" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* TABLE */}
      <Box>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f7fb',
              fontWeight: 'bold'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9fbff'
            }
          }}
        />
      </Box>

      <CoordinatorMapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        coordinator={selectedCoordinator}
        academicYear={selectedAcademicYear}
        statusByTeamFilter={statusByTeamFilter}
        clientBillingStatusFilter={clientBillingStatusFilter}
        selectedService={selectedService}
        orderSource={orderSource}
        props={props}
      />

    </Box>
  )
}

export default CoordinatorWiseReportList;